import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Layers3, Hammer, Cog, Truck,
  ShieldCheck, Wrench, TreePine,
} from "lucide-react";
import { SectionHeading, Eyebrow } from "../components/SectionHeading";
import SEO from "../components/SEO";
import { useSiteServices } from "../hooks/useSiteContent";
import { resolveAssetUrl } from "../lib/api";

const ICON_MAP = { Layers3, Hammer, Cog, Truck, ShieldCheck, Wrench, TreePine };

const STEEL_IMG = "/media/photos/steel-framework.jpg";

const Services = () => {
  const services = useSiteServices() || [];
  const lumber = services.find((s) => s.kind === "lumber");
  const steel = services.find((s) => s.kind === "steel");
  const secondary = services.filter((s) => s.kind === "secondary").sort((a, b) => a.order - b.order);

  return (
    <div data-testid="services-page">
      <SEO
        title="Services - Lumber, Structural Steel & Material Supply"
        path="/services"
        description="Full material-supply services for builders & GCs: dimensional lumber, engineered wood, structural steel, trusses, fasteners, and job-site delivery available nationwide."
      />

      <section className="bg-[#0A192F] py-20 sm:py-24 lg:py-32 relative overflow-hidden" data-testid="services-header">
        <div className="absolute inset-0 opacity-15">
          <img src={STEEL_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <Eyebrow light>Our Capabilities</Eyebrow>
          <h1 className="mt-5 sm:mt-6 font-display font-black tracking-tighter text-white text-4xl sm:text-6xl lg:text-7xl max-w-4xl">
            Material Supply for Every <span className="text-[#D4AF37]">Phase of the Build.</span>
          </h1>
          <p className="mt-6 sm:mt-8 text-white/75 max-w-2xl text-base sm:text-lg">
            From foundation to framing to finish - CM Lindley supplies the materials that move
            your project forward. Specializing in lumber and structural steel with everything
            you need in between, CM Lindley serves builders and general contractors{" "}
            <span className="text-[#D4AF37] font-semibold">nationwide</span>.
          </p>
        </div>
      </section>

      {lumber && <FeaturedService item={lumber} anchor="lumber" reverse={false} />}
      {steel && <FeaturedService item={steel} anchor="steel" reverse />}

      <section className="py-20 sm:py-24 lg:py-32 bg-white" data-testid="services-secondary">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <SectionHeading
            eyebrow="Full Build Support"
            title="Beyond the basics."
            subtitle="Whatever the punch list calls for, CM Lindley likely supplies it. Talk to us about your full material needs - we'll source it and stage it."
          />
          <div className="mt-12 sm:mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {secondary.map((s, i) => {
              const Icon = ICON_MAP[s.icon] || Cog;
              const isDark = i % 2 === 0;
              const bg = isDark ? "bg-[#0A192F]" : "bg-[#D4AF37]";
              const text = isDark ? "text-white" : "text-[#0A192F]";
              const iconBg = isDark ? "bg-[#D4AF37]" : "bg-[#0A192F]";
              const iconColor = isDark ? "text-[#0A192F]" : "text-[#D4AF37]";
              return (
                <div
                  key={s.id}
                  data-testid={`secondary-service-${i}`}
                  className={`${bg} ${text} p-7 sm:p-8 hover-lift transition-transform`}
                >
                  <div className={`w-14 h-14 ${iconBg} flex items-center justify-center`}>
                    <Icon className={iconColor} size={28} strokeWidth={2.25} />
                  </div>
                  <h3 className={`mt-6 sm:mt-7 font-display font-black text-xl sm:text-2xl tracking-tight ${text}`}>
                    {s.title}
                  </h3>
                  <p className={`mt-3 text-sm leading-relaxed ${text} opacity-90`}>{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#0A192F] py-20 sm:py-24" data-testid="services-cta">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 text-center">
          <h2 className="font-display font-black text-white text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
            Got a takeoff? Send it our way.
          </h2>
          <p className="mt-4 sm:mt-5 text-white/75 max-w-2xl mx-auto text-sm sm:text-base">
            Drop us a plan set, a cut list, or just a phone call. We'll price the package and have material on your site when you need it.
          </p>
          <Link
            to="/contact"
            data-testid="services-bottom-cta"
            className="mt-7 sm:mt-10 inline-flex w-full sm:w-auto items-center justify-center gap-3 bg-[#D4AF37] text-[#0A192F] px-8 sm:px-10 py-4 sm:py-5 font-display text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] font-bold hover:bg-white transition-colors"
          >
            Submit a Quote <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

const FeaturedService = ({ item, anchor, reverse }) => {
  return (
    <section
      id={anchor}
      className={`py-20 sm:py-24 lg:py-32 scroll-mt-24 ${reverse ? "bg-[#F0F4F8]" : "bg-white"}`}
      data-testid={`service-${anchor}`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div className={reverse ? "lg:order-2" : ""}>
          <Eyebrow>{anchor === "lumber" ? "Lumber" : "Structural Steel"}</Eyebrow>
          <h2 className="mt-4 sm:mt-5 font-display font-black tracking-tighter text-[#0A192F] text-3xl sm:text-4xl lg:text-5xl">
            {item.title}
          </h2>
          <p className="mt-5 sm:mt-6 text-[#0A192F]/75 leading-relaxed">{item.body}</p>
          {item.features?.length > 0 && (
            <ul className="mt-7 sm:mt-8 grid sm:grid-cols-2 gap-3">
              {item.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-[#0A192F]/80">
                  <Check size={16} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}
          <Link
            to="/contact"
            data-testid={`${anchor}-cta`}
            className="mt-7 sm:mt-10 inline-flex w-full sm:w-auto items-center justify-center gap-3 bg-[#0A192F] text-white px-7 sm:px-8 py-4 font-display text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] font-bold hover:bg-[#D4AF37] hover:text-[#0A192F] transition-colors"
          >
            Quote {anchor === "lumber" ? "Lumber" : "Steel"} Package <ArrowRight size={16} />
          </Link>
        </div>
        <div className={`aspect-[4/5] overflow-hidden ${reverse ? "lg:order-1" : ""}`}>
          <img src={resolveAssetUrl(item.image_url)} alt={item.title} loading="lazy" className="w-full h-full object-cover" />
        </div>
      </div>
    </section>
  );
};

export default Services;
